import { InputHTMLAttributes, forwardRef, useState } from "react";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";

interface InputFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  extra?: string;
  extraContainer?: string;
  variant?: "solid" | "outline";
  state?: "error" | "success"; // Specific states for styling
  readonly?: boolean;
  sizes?: "lg" | "md" | "sm" | "xs";
  onChange?: (val: any) => void;
  color?: "primary" | "secondary" | "white";
  rounded?: string;
  loading?: boolean;
  secure?: boolean; // New prop to indicate password/secure input
}

const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      id,
      value,
      type = "text",
      placeholder,
      sizes = "lg",
      variant = "outline",
      color = "primary",
      rounded = "rounded-2xl",
      state,
      disabled,
      readonly,
      loading = false,
      extra = "",
      extraContainer = "",
      onChange,
      onBlur,
      onFocus,
      secure = false,
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const [showPassword, setShowPassword] = useState(false);
    const isPasswordType = secure || type === "password";
    const inputType = isPasswordType
      ? showPassword
        ? "text"
        : "password"
      : type;

    const colorClasses = {
      primary: {
        solid: `text-primary bg-gray-50 placeholder:text-gray-500`,
        outline: `border border-primary text-primary bg-transparent focus:bg-white placeholder:text-gray-500`,
      },
      secondary: {
        solid: `text-secondary bg-gray-50 placeholder:text-gray-500`,
        outline: `border border-secondary text-secondary bg-transparent focus:bg-white placeholder:text-gray-500`,
      },
      white: {
        solid: `text-primary bg-white placeholder:text-gray-500`,
        outline: `border border-white text-primary bg-transparent focus:bg-white placeholder:text-gray-500`,
      },
    };

    const sizeClasses = {
      lg: "h-12 px-6 text-txt-lg",
      md: "h-10 px-4 text-txt-md",
      sm: "h-8 px-3 text-txt-sm",
      xs: "h-6 px-2 text-txt-xs",
    };

    const stateClasses = {
      error: "outline-red-500 text-red-500 placeholder:text-red-500",
      success: "outline-green-500 text-green-500 placeholder:text-green-500",
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      const parsedValue =
        type === "number" && !isNaN(Number(inputValue))
          ? parseFloat(inputValue)
          : inputValue;

      onChange?.(parsedValue);
    };

    return (
      <div className={`${extraContainer} flex w-full items-center`}>
        <input
          {...rest}
          ref={ref}
          id={id}
          type={inputType}
          value={value !== undefined ? String(value) : ""}
          placeholder={readonly ? "" : placeholder}
          readOnly={readonly}
          disabled={isDisabled}
          onChange={handleChange}
          onFocus={onFocus}
          onBlur={onBlur}
          className={`flex w-full items-center justify-center  ${rounded} ${
            sizeClasses[sizes]
          } ${colorClasses[color][variant]} ${
            state ? stateClasses[state] : "outline-none"
          } ${
            isDisabled
              ? "border-none bg-gray-50/50"
              : "hover:opacity-50 focus:opacity-100"
          } ${extra}`}
        />
        {isPasswordType && (
          <button
            type="button"
            tabIndex={-1}
            className="-ml-10 text-xl text-gray-400 hover:text-gray-600 focus:outline-none"
            onClick={() => setShowPassword((v) => !v)}
            disabled={isDisabled}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
          </button>
        )}
      </div>
    );
  }
);

export default InputField;
